<template>
    <div class="relative-date-range-slider" :class="{ disabled: props.disabled }">
        <div class="slider-row">
            <div class="slider-shell">
                <slider
                    :model-value="orderedRange"
                    range
                    :min="props.min"
                    :max="props.max"
                    :step="1"
                    :disabled="props.disabled"
                    @update:model-value="updateRange"
                />
                <div class="today-marker" :style="{ left: todayMarkerPosition }">
                    <span>today</span>
                </div>
            </div>
            <primevue-button
                class="plain-btn reset-button"
                :disabled="props.disabled"
                v-tooltip.top="props.customResetButtonText"
                @click="resetRange"
            >
                <font-awesome-icon :icon="['fas', 'rotate-left']" />
            </primevue-button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import PrimevueButton from 'primevue/button';
import Slider from 'primevue/slider';

const props = withDefaults(defineProps<{
    modelValue: [number, number];
    defaultValue?: [number, number];
    disabled?: boolean;
    min?: number;
    max?: number;
    customResetButtonText?: string;
}>(), {
    defaultValue: () => [-7, 14],
    disabled: false,
    min: -28,
    max: 28,
    customResetButtonText: 'Reset to default range',
});

const emit = defineEmits<{
    'update:modelValue': [[number, number]];
}>();

const orderedRange = computed<[number, number]>(() => orderRange(props.modelValue));
const todayMarkerPosition = computed<string>(() => {
    const rangeSize = props.max - props.min;
    if (rangeSize <= 0) return '50%';

    return `${((-props.min / rangeSize) * 100).toFixed(2)}%`;
});

function orderRange (range: [number, number]): [number, number] {
    return range[0] <= range[1] ? range : [range[1], range[0]];
}

function updateRange (value: number | number[]): void {
    if (!Array.isArray(value) || value.length !== 2) return;
    emit('update:modelValue', orderRange([value[0], value[1]]));
}

function resetRange (): void {
    emit('update:modelValue', orderRange(props.defaultValue));
}

</script>

<style lang="scss" scoped>
.relative-date-range-slider {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
    min-width: 0;

    &.disabled {
        opacity: 0.65;
    }

    .slider-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: var(--smallSpacing);
        align-items: center;
    }

    .slider-shell {
        position: relative;
        min-width: 0;
        padding: var(--smallSpacing) var(--defaultSpacing) var(--defaultSpacing);
        background-color: var(--neutral200);
        border-radius: var(--borderRadiusOuter);
    }

    .today-marker {
        position: absolute;
        bottom: 0;
        width: 1px;
        height: 1rem;
        background-color: var(--neutral500);
        transform: translateX(-50%);

        span {
            position: absolute;
            top: 0.8rem;
            left: 50%;
            transform: translateX(-50%);
            color: var(--neutral600);
            font-size: var(--extraSmallFontSize);
            font-weight: var(--boldFontWeight);
            text-transform: uppercase;
        }
    }

    .reset-button {
        min-width: 2.5rem;
        height: 2.5rem;
        padding: 0;
        justify-content: center;
    }

    :deep(.p-slider) {
        margin: var(--smallSpacing) 0 var(--defaultSpacing);
    }
}
</style>
